import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, X, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";
const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

// Pass props to these components
const Notification = ({ showNotification, notification, isDarkMode }) => {
  if (!showNotification) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div
        className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border ${
          notification.type === "success"
            ? isDarkMode
              ? "bg-green-500/10 border-green-500/30 text-green-400"
              : "bg-green-100 border-green-300 text-green-700"
            : isDarkMode
            ? "bg-red-500/10 border-red-500/30 text-red-400"
            : "bg-red-100 border-red-300 text-red-700"
        }`}
      >
        {notification.type === "success" ? (
          <CheckCircle size={20} />
        ) : (
          <AlertCircle size={20} />
        )}
        <p className="font-medium">{notification.message}</p>
      </div>
    </div>
  );
};

const ConfirmModal = ({
  showConfirmModal,
  isDarkMode,
  actionType,
  suspensionReason,
  closeModals,
  handleConfirmAction,
}) => {
  if (!showConfirmModal) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
      <div
        className={`rounded-2xl p-6 max-w-md w-full shadow-2xl ${
          isDarkMode
            ? "bg-black border border-zinc-900"
            : "bg-white border border-gray-200"
        }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                actionType === "activate" ? "bg-green-500/20" : "bg-red-500/20"
              }`}
            >
              <AlertCircle
                size={24}
                className={
                  actionType === "activate" ? "text-green-400" : "text-red-400"
                }
              />
            </div>
            <h2
              className={`text-xl font-bold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {actionType === "activate" ? "Activate User" : "Suspend User"}
            </h2>
          </div>
          <button
            onClick={closeModals}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        <p className={`mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-700"}`}>
          {actionType === "activate"
            ? "Are you sure you want to activate this user? They will regain full access to the platform."
            : `Are you sure you want to suspend this user? ${
                suspensionReason
                  ? "They will be notified with the reason provided."
                  : ""
              }`}
        </p>

        {actionType === "suspend" && suspensionReason && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              isDarkMode
                ? "bg-red-500/10 border border-red-500/30"
                : "bg-red-100 border border-red-300"
            }`}
          >
            <p
              className={`text-sm mb-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Suspension Reason:
            </p>
            <p
              className={`break-words ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {suspensionReason}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={closeModals}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
              isDarkMode
                ? "bg-zinc-900 hover:bg-zinc-800 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-900"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmAction}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              actionType === "activate"
                ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/20"
                : "bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-lg shadow-red-500/20"
            }`}
          >
            {actionType === "activate" ? "Activate" : "Suspend"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReasonModal = ({
  showReasonModal,
  isDarkMode,
  suspensionReason,
  setSuspensionReason,
  closeModals,
  handleReasonSubmit,
}) => {
  if (!showReasonModal) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4">
      <div
        className={`rounded-2xl p-6 max-w-md w-full shadow-2xl ${
          isDarkMode
            ? "bg-black border border-zinc-900"
            : "bg-white border border-gray-200"
        }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
              <AlertCircle size={24} className="text-red-400" />
            </div>
            <h2
              className={`text-xl font-bold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Suspension Reason
            </h2>
          </div>
          <button
            onClick={closeModals}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        <p className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-700"}`}>
          Please provide a reason for suspending this user. This will help
          maintain transparency and accountability.
        </p>

        <textarea
          value={suspensionReason}
          onChange={(e) => setSuspensionReason(e.target.value)}
          placeholder="Enter suspension reason..."
          className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all min-h-[120px] resize-none ${
            isDarkMode
              ? "bg-black border border-zinc-800 text-white placeholder-gray-500 focus:border-red-500/50"
              : "bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-red-500"
          }`}
          maxLength={500}
        />
        <p
          className={`text-xs mt-2 text-right ${
            isDarkMode ? "text-gray-500" : "text-gray-600"
          }`}
        >
          {suspensionReason.length}/500 characters
        </p>

        <div className="flex gap-3 mt-6">
          <button
            onClick={closeModals}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
              isDarkMode
                ? "bg-zinc-900 hover:bg-zinc-800 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-900"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleReasonSubmit}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-lg font-semibold transition-all shadow-lg shadow-red-500/20"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const SlayDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Campaigns");
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [actionType, setActionType] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });

  const tabs = ["Campaigns", "Badges"];
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    const fetchUserDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/users/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (
          response.data.statusCode === 401 ||
          response.data.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        setUser(response.data);
      } catch (err) {
        if (
          err.response?.status === 401 ||
          err.response?.data?.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        console.error("Failed to fetch user details:", err);
        const errorMessage =
          err.response?.data?.message ||
          "Failed to load user details. Please try again later.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserDetails();
    }
  }, [userId, navigate]);

  useEffect(() => {
    if (activeTab === "Campaigns" && userId) {
      fetchUserCampaigns();
    }
  }, [activeTab, userId]);

  const fetchUserCampaigns = async () => {
    setCampaignsLoading(true);
    const accessToken = localStorage.getItem("accessToken");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/users/${userId}/campaigns`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const showNotificationMessage = (type, message) => {
    setNotification({ type, message });
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  const handleStatusButtonClick = () => {
    const isSuspended = user.status === "suspended";
    setActionType(isSuspended ? "activate" : "suspend");

    if (isSuspended) {
      setShowConfirmModal(true);
    } else {
      setShowReasonModal(true);
    }
  };

  const handleConfirmAction = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    const newStatus = actionType === "activate" ? "active" : "suspended";

    setIsUpdatingStatus(true);
    setShowConfirmModal(false);
    setShowReasonModal(false);

    try {
      const payload = { status: newStatus };

      if (actionType === "suspend" && suspensionReason.trim()) {
        payload.reason = suspensionReason.trim();
      }

      await axios.patch(
        `${API_BASE_URL}/admin/users/${userId}/status`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setUser((prevUser) => ({
        ...prevUser,
        status: newStatus,
      }));

      showNotificationMessage(
        "success",
        `User ${
          actionType === "activate" ? "activated" : "suspended"
        } successfully!`
      );

      setSuspensionReason("");
    } catch (err) {
      console.error("Failed to update user status:", err);
      showNotificationMessage(
        "error",
        `Failed to ${actionType} user. Please try again.`
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleReasonSubmit = () => {
    if (!suspensionReason.trim()) {
      showNotificationMessage(
        "error",
        "Please provide a reason for suspension"
      );
      return;
    }
    setShowReasonModal(false);
    setShowConfirmModal(true);
  };

  const closeModals = () => {
    setShowConfirmModal(false);
    setShowReasonModal(false);
    setSuspensionReason("");
  };

  const UserHeader = () => {
    if (!user) return null;

    const avatar = `${user.firstName?.charAt(0) || ""}${
      user.lastName?.charAt(0) || ""
    }`.toUpperCase();

    const followers = user.followersCount?.toLocaleString() || "0";
    const following = user.followingCount?.toLocaleString() || "0";
    const posts = user.postsCount?.toLocaleString() || "0";

    const isSuspended = user.status === "suspended";
    const isActive = user.status === "active";

    return (
      <div className={`p-6 md:p-8 ${isDarkMode ? "bg-black" : "bg-white"}`}>
        {" "}
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
          {user.profilePicture ? (
            <img
              src={user.profilePicture}
              alt={`${user.firstName} ${user.lastName}`}
              className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-orange-400 object-cover"
            />
          ) : (
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-orange-400 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-4xl">
              {avatar}
            </div>
          )}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center gap-3 justify-center md:justify-start flex-wrap">
              <h1
                className={`text-3xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {user.firstName} {user.lastName}
              </h1>
              {isSuspended && (
                <span className="px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded-full text-xs font-semibold">
                  Suspended
                </span>
              )}
              {isActive && (
                <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-xs font-semibold">
                  Active
                </span>
              )}
            </div>
            <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
              {" "}
              @{user.username || "user"}
            </p>
            <div
              className={`flex flex-col md:flex-row gap-x-4 text-sm mt-2 ${
                isDarkMode ? "text-gray-500" : "text-gray-600"
              }`}
            >
              {user.phoneNumber && <span>{user.phoneNumber}</span>}
              {user.email && <span>{user.email}</span>}
            </div>
          </div>
          <div className="flex gap-8 text-center">
            <div>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {followers}
              </p>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-500" : "text-gray-600"
                }`}
              >
                Follower
              </p>
            </div>
            <div>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {following}
              </p>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-500" : "text-gray-600"
                }`}
              >
                Following
              </p>
            </div>
            <div>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {posts}
              </p>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-500" : "text-gray-600"
                }`}
              >
                Post
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6 gap-3">
          <button
            onClick={handleStatusButtonClick}
            disabled={isUpdatingStatus}
            className={`px-8 py-3 rounded-xl font-bold transition-all transform hover:scale-105 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
              isSuspended
                ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-green-500/30"
                : "bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-red-500/30"
            }`}
          >
            {isUpdatingStatus
              ? "Updating..."
              : isSuspended
              ? "Activate User"
              : "Suspend User"}
          </button>
        </div>
      </div>
    );
  };

  const TabNavigation = () => (
    <nav
      className={`flex items-center gap-8 border-b px-6 md:px-8 ${
        isDarkMode ? "border-zinc-900 bg-black" : "border-gray-200 bg-white"
      }`}
    >
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => setActiveTab(tab)}
          className={`py-4 text-sm font-semibold transition-colors duration-300 ${
            activeTab === tab
              ? "text-orange-400 border-b-2 border-orange-400"
              : isDarkMode
              ? "text-gray-500 hover:text-orange-400"
              : "text-gray-600 hover:text-orange-500"
          }`}
        >
          {tab}
        </button>
      ))}
    </nav>
  );

  const BadgesSection = () => {
    return (
      <div className={`p-6 md:p-8 ${isDarkMode ? "bg-black" : "bg-white"}`}>
        {" "}
        <div className="text-center">
          <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
            {" "}
            Badges section coming soon...
          </p>
        </div>
      </div>
    );
  };

  const CampaignsSection = () => {
    if (campaignsLoading) {
      return (
        <div
          className={`p-6 md:p-8 text-center ${
            isDarkMode ? "bg-black" : "bg-white"
          }`}
        >
          <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
            {" "}
            Loading campaigns...
          </p>
        </div>
      );
    }

    if (campaigns.length === 0) {
      return (
        <div
          className={`p-6 md:p-8 text-center ${
            isDarkMode ? "bg-black" : "bg-white"
          }`}
        >
          <p className={isDarkMode ? "text-gray-500" : "text-gray-600"}>
            {" "}
            No campaigns found for this user.
          </p>
        </div>
      );
    }

    return (
      <div className={`p-6 md:p-8 ${isDarkMode ? "bg-black" : "bg-white"}`}>
        {" "}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {campaigns.map((campaign) => {
            const statusColors = {
              approved: "bg-green-500/20 text-green-400 border-green-500/30",
              rejected: "bg-red-500/20 text-red-400 border-red-500/30",
              pending: "bg-orange-500/20 text-orange-400 border-orange-500/30",
              active: "bg-blue-500/20 text-blue-400 border-blue-500/30",
              paused: "bg-gray-500/20 text-gray-400 border-gray-500/30",
              completed:
                "bg-purple-500/20 text-purple-400 border-purple-500/30",
            };

            const statusLabel =
              campaign.approvalStatus || campaign.status || "unknown";
            const statusClass =
              statusColors[statusLabel.toLowerCase()] || statusColors.pending;

            return (
              <div
                key={campaign._id}
                className={`rounded-lg p-5 hover:border-orange-400 transition-all cursor-pointer ${
                  isDarkMode
                    ? "bg-black border border-zinc-900"
                    : "bg-gray-50 border border-gray-200"
                }`}
                onClick={() => navigate(`/campaigns/${campaign._id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3
                    className={`font-semibold text-lg line-clamp-1 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {campaign.title}
                  </h3>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium border ${statusClass}`}
                  >
                    {statusLabel}
                  </span>
                </div>

                <p
                  className={`text-sm mb-4 line-clamp-2 ${
                    isDarkMode ? "text-gray-500" : "text-gray-600"
                  }`}
                >
                  {campaign.description}
                </p>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-500" : "text-gray-600"}
                    >
                      Budget:
                    </span>
                    <span
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      ${campaign.budget?.toLocaleString() || "0"}
                    </span>
                  </div>
                  {campaign.startDate && (
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-600"
                        }
                      >
                        Start:
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {new Date(campaign.startDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {campaign.endDate && (
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-600"
                        }
                      >
                        End:
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {new Date(campaign.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {campaign.rejectionReason && (
                  <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded">
                    <p className="text-red-400 text-xs">
                      <strong>Rejection Reason:</strong>{" "}
                      {campaign.rejectionReason}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <p className="text-xl">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div className="flex-grow flex items-center justify-center p-5">
          <div className="text-center">
            <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 text-red-200 max-w-md mb-4">
              {error}
            </div>
            <button
              onClick={() => navigate(-1)}
              className="bg-orange-400 text-black px-6 py-2 rounded-lg hover:bg-orange-500 transition-colors font-semibold"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <div className="text-center">
            <p className="text-xl mb-4">User not found.</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-orange-400 text-black px-6 py-2 rounded-lg hover:bg-orange-500 transition-colors font-semibold"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="flex flex-col gap-5 p-5">
        <Notification
          showNotification={showNotification}
          notification={notification}
          isDarkMode={isDarkMode}
        />
        <ConfirmModal
          showConfirmModal={showConfirmModal}
          isDarkMode={isDarkMode}
          actionType={actionType}
          suspensionReason={suspensionReason}
          closeModals={closeModals}
          handleConfirmAction={handleConfirmAction}
        />
        <ReasonModal
          showReasonModal={showReasonModal}
          isDarkMode={isDarkMode}
          suspensionReason={suspensionReason}
          setSuspensionReason={setSuspensionReason}
          closeModals={closeModals}
          handleReasonSubmit={handleReasonSubmit}
        />

        <button
          onClick={() => navigate("/user-management")}
          className={`flex items-center gap-2 w-fit transition-colors ${
            isDarkMode
              ? "text-gray-500 hover:text-white"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <ChevronLeft size={20} />
          <span className="text-sm font-medium">Back to Users</span>
        </button>

        <div
          className={`border rounded-2xl overflow-hidden ${
            isDarkMode ? "bg-black border-zinc-900" : "bg-white border-gray-200"
          }`}
        >
          <UserHeader />
          <TabNavigation />
          {activeTab === "Campaigns" && <CampaignsSection />}
          {activeTab === "Badges" && <BadgesSection />}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SlayDetails;
